import { FilteredItemProps } from '@delmonicos/shared-design-system-react/src/components/filtered-item/filtered-item';
import { GetNetworksPaginatedModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/networks/models/inputs/get-networks-paginated.model';
import { GatewayNetworkModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/networks/models/outputs/gateway-network.model';
import {
  ColumnDef,
  ColumnOrderState,
  VisibilityState,
  createColumnHelper,
} from '@tanstack/react-table';

import { CustomerFilterStateController } from '@app/feature/customer/customer-filter-state/customer-filter-state.controller';

import { NetworkListCustomerColumn } from './network-list-customer.column';
import { NetworkListDescriptionColumn } from './network-list-description.column';
import { NetworkListIdColumn } from './network-list-id.column';
import { NetworkListNameColumn } from './network-list-name.column';

// /////////////////////////////////////////////////////////////////////////////
// SORTING
// /////////////////////////////////////////////////////////////////////////////

export const NETWORK_LIST_SORT_FIELD: NonNullable<
  GetNetworksPaginatedModel['sortField']
> = 'createdAt';

export const NETWORK_LIST_SORT_ORDER: NonNullable<
  GetNetworksPaginatedModel['sortOrder']
> = 1;

// /////////////////////////////////////////////////////////////////////////////
// FILTERING
// /////////////////////////////////////////////////////////////////////////////

type ResourceListFilter = Partial<
  Record<
    keyof GetNetworksPaginatedModel,
    {
      componentType: React.ComponentType<{
        value: any;
        onRemove: FilteredItemProps['onRemove'];
      }>;
    }
  >
>;

export const NETWORK_LIST_FILTER: ResourceListFilter = {
  customerId: {
    componentType: CustomerFilterStateController,
  },
} as const;

export const NETWORK_LIST_FILTER_NAME_LIST = Object.keys(
  NETWORK_LIST_FILTER,
) as Array<keyof typeof NETWORK_LIST_FILTER>;

// /////////////////////////////////////////////////////////////////////////////
// DISPLAYING
// /////////////////////////////////////////////////////////////////////////////

export const networkListColumnHelper =
  createColumnHelper<GatewayNetworkModel>();

type ResourceColumnType = ColumnDef<GatewayNetworkModel, any>[];

export const NETWORK_LIST_COLUMN_LIST: ResourceColumnType = [
  networkListColumnHelper.accessor('id', NetworkListIdColumn),
  networkListColumnHelper.accessor('name', NetworkListNameColumn),
  networkListColumnHelper.accessor('description', NetworkListDescriptionColumn),
  networkListColumnHelper.accessor('customer', NetworkListCustomerColumn),
];

export const NETWORK_LIST_COLUMN_ID_LIST = NETWORK_LIST_COLUMN_LIST.flatMap(
  (columnDef) => {
    if ('accessorKey' in columnDef) {
      return [columnDef.id ?? (columnDef.accessorKey as string)];
    }

    if (columnDef.id) {
      return [columnDef.id];
    }

    return [];
  },
);

export const NETWORK_LIST_COLUMN_ORDER = [...NETWORK_LIST_COLUMN_ID_LIST];

export const NETWORK_LIST_COLUMN_VISIBILITY: VisibilityState =
  NETWORK_LIST_COLUMN_ID_LIST.reduce(
    (columnVisibility, columnId) => ({
      ...columnVisibility,
      [columnId]: true,
    }),
    {} as VisibilityState,
  );

// /////////////////////////////////////////////////////////////////////////////
// OPTIONS
// /////////////////////////////////////////////////////////////////////////////

export interface NetworkListOptionsModel {
  columnVisibility: VisibilityState;
  columnOrder: ColumnOrderState;
}

export const NETWORK_LIST_DEFAULT_OPTIONS: NetworkListOptionsModel = {
  columnVisibility: NETWORK_LIST_COLUMN_VISIBILITY,
  columnOrder: NETWORK_LIST_COLUMN_ORDER,
};

export const NETWORK_LIST_COLUMN_LABEL_I18N_KEY: Record<string, string> = {
  id: 'delmonicos-shared-sdk.outputs-base.id',
  name: 'delmonicos-shared-sdk.outputs-network.name',
  description: 'delmonicos-shared-sdk.outputs-network.description',
  customer: 'delmonicos-shared-sdk.outputs-gateway-network.customer',
};
